// v4 — Trend Catcher
import React, { useState } from 'react';
import { v4data, seriesRange } from './v4data';
import { AreaChart } from './viz';
import { PageHeader, Card, StatCard, Delta, fmt$, fmtN, fmtX, fmt$2, pct } from './v4ui';
import markPhoto from 'static/images/v4/mark.png';
import './dashboard.scss';

const METRICS = {
  Revenue: { key: 'revenue', fmt: fmt$, color: '#5A54F3', agg: 'sum' },
  ROAS: { key: 'roas', fmt: fmtX, color: '#8AE8BD', agg: 'avg' },
  Orders: { key: 'orders', fmt: fmtN, color: '#38bdf8', agg: 'sum' },
  AOV: { key: 'aov', fmt: fmt$2, color: '#f472b6', agg: 'avg' },
};

export default function TrendsV4() {
  const D = v4data(30);
  const [metric, setMetric] = useState('Revenue');
  const M = METRICS[metric];

  const range = (a, b) => seriesRange(D.ds.accountDaily, D.dates[Math.max(0, D.last - a)], D.dates[Math.max(0, D.last - b)]).map((d) => d.totals[M.key]);
  const last7 = range(6, 0);
  const prev7 = range(13, 7);
  const lastMonth7 = range(36, 30);
  const agg = (arr) => (M.agg === 'sum' ? arr.reduce((a, b) => a + b, 0) : arr.reduce((a, b) => a + b, 0) / Math.max(1, arr.length));

  const vWeek = pct(agg(last7), agg(prev7));
  const vMonth = pct(agg(last7), agg(lastMonth7));
  const labels = ['6d', '5d', '4d', '3d', '2d', 'Yest', 'Today'];

  return (
    <div className="v4-dash">
      <PageHeader title="Trend Catcher" sub="Period-over-period momentum across your key metrics" live />

      <section className="v4-metric-tabs">
        {Object.keys(METRICS).map((m) => (
          <button key={m} className={'v4-mtab ' + (metric === m ? 'active' : '')} onClick={() => setMetric(m)}>{m}</button>
        ))}
      </section>

      <section className="v4-trend-grid">
        {/* row 1 — three stat cards */}
        <StatCard label={`${metric} · this week`} value={M.fmt(agg(last7))} color={M.color} />
        <div className="v4-stat"><div className="l">vs last week</div><div className="v"><Delta value={vWeek} /></div><div className="v4-muted" style={{ marginTop: 4 }}>{M.fmt(agg(prev7))} prior</div></div>
        <div className="v4-stat"><div className="l">vs same week last month</div><div className="v"><Delta value={vMonth} /></div><div className="v4-muted" style={{ marginTop: 4 }}>{M.fmt(agg(lastMonth7))} then</div></div>

        {/* row 2 — big Mark card (under "this week") + graph (under the two comparison cards) */}
        <div className="v4-trend-mark">
          <div className="tm-head">
            <img src={markPhoto} alt="Mark" />
            <div className="tm-id"><b>Mark</b><span>AI Media Buyer</span></div>
            <span className="tm-live"><i /> live</span>
          </div>
          <p className="tm-text">
            {vWeek >= 0
              ? <><b>{metric} is up {Math.abs(vWeek).toFixed(1)}% on last week{vMonth >= 0 ? ` and ${Math.abs(vMonth).toFixed(1)}% over the same week last month` : ''}.</b> That's real momentum and I'm not sitting on it. I'm pushing more budget into the exact days and creatives driving the lift while holding your efficiency steady. Approve the moves and I'll keep pressing while the trend is on our side.</>
              : <><b>{metric} is down {Math.abs(vWeek).toFixed(1)}% versus last week.</b> Momentum's cooling and I'm on it. I'm pulling spend off the segments that are fading and reloading into the ones still converting hard. Approve the moves and I'll defend the number before it slips further.</>}
          </p>
          <div className="tm-actions">
            <button className="mark-approve">Approve the moves</button>
            <button className="mark-ghost">Show me the math</button>
          </div>
        </div>

        <Card className="v4-trend-graph" title={`${metric} — 7-day comparison`} sub="This week vs last week vs same week last month"
          right={<div className="v4-legend"><span><i style={{ background: M.color }} /> This week</span><span><i style={{ background: '#38bdf8' }} /> Last week</span><span><i style={{ background: '#6a6a82' }} /> Last month</span></div>}>
          <AreaChart height={300} labels={labels} series={[
            { name: 'This week', color: M.color, data: last7, fill: true },
            { name: 'Last week', color: '#38bdf8', data: prev7, fill: false },
            { name: 'Last month', color: '#6a6a82', data: lastMonth7, fill: false },
          ]} />
        </Card>
      </section>
    </div>
  );
}
